import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import SVGFingerprint from "./FingerPrint";
import SVGFakeFingerPrint1 from "./FakeFingerPrint1";
import SVGFakeFingerPrint2 from "./FakeFingerPrint2";
import SVGFakeFingerPrint3 from "./FakeFingerPrint3";

// ─── Couleurs joueurs ─────────────────────────────────────────────────────────
function getColor(v) {
  const colors = ["#22d3ee", "#d946ef", "#fbbf24", "#34d399", "#818cf8", "#f97316"];
  return colors[(Number(v) - 1) % colors.length];
}

// Convertit un hex "#rrggbb" en { r, g, b } 0..1
function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return { r: ((n >> 16) & 0xff) / 255, g: ((n >> 8) & 0xff) / 255, b: (n & 0xff) / 255 };
}

// ─── Sources SVG ──────────────────────────────────────────────────────────────
// yTop/yBot : zone utile dans les coordonnées du viewBox de chaque SVG
const SVG_SOURCES = {
  real: { Component: SVGFingerprint, vb: "0 0 800 800", vw: 800, vh: 800, yTop: 72, yBot: 748 },
  fake1: { Component: SVGFakeFingerPrint1, vb: "0 0 450 500", vw: 450, vh: 500, yTop: 22, yBot: 480 },
  fake2: { Component: SVGFakeFingerPrint2, vb: "0 0 450 500", vw: 450, vh: 500, yTop: 408, yBot: 478 },
  fake3: { Component: SVGFakeFingerPrint3, vb: "0 0 128 128", vw: 128, vh: 128, yTop: 0, yBot: 128 },
};

function getBand(fragIdx, total, srcKey) {
  const { yTop, yBot, vh } = SVG_SOURCES[srcKey];
  const h = (yBot - yTop) / total;
  // Premier fragment : on remonte jusqu'au bord haut (y=0)
  // Dernier fragment : on descend jusqu'au bord bas (y=vh)
  const y1 = fragIdx === 0 ? 0 : yTop + fragIdx * h;
  const y2 = fragIdx === total - 1 ? vh : yTop + (fragIdx + 1) * h;
  return { y1, y2 };
}

// ─── UID ──────────────────────────────────────────────────────────────────────
let _uid = 0;
function useUID() { return useRef(`fpx_${++_uid}`).current; }

// ─── BandedSVG ────────────────────────────────────────────────────────────────
// Rend un SVG source clipé sur une bande, dans la couleur voulue.
// Utilise un feColorMatrix SVG pour forcer la couleur indépendamment des
// fill inline, classes CSS internes, etc. — fonctionne sur TOUS les SVG sources.
function BandedSVG({ srcKey, band, color, width, height, opacity = 1 }) {
  const uid = useUID();
  const { Component, vb, vw, yTop, yBot } = SVG_SOURCES[srcKey];

  // feColorMatrix "matrix" : remplace toutes les couleurs par `color`
  // Formule : chaque channel de sortie = R*r + G*g + B*b + A*0 + 0
  // Avec r,g,b = composantes de la couleur cible
  // On cible l'alpha : on garde l'alpha original, on force RGB vers la couleur
  const { r, g, b } = hexToRgb(color);
  // feFlood + feComposite est plus fiable : flood avec la couleur, composite "in" avec alpha source
  const filterId = `${uid}_color`;
  const clipId = band ? `${uid}_clip` : null;

  return (
    <div style={{ width, height, overflow: "hidden", opacity, flexShrink: 0, position: "relative" }}>
      <svg
        viewBox={vb}
        width={width}
        height={height}
        style={{ display: "block", position: "absolute", top: 0, left: 0 }}
      >
        <defs>
          <filter id={filterId} x="0%" y="0%" width="100%" height="100%" colorInterpolationFilters="sRGB">
            {/* Étape 1 : extraire l'alpha du graphique source */}
            <feColorMatrix type="matrix"
              values="0 0 0 0 0
                      0 0 0 0 0
                      0 0 0 0 0
                      0.3 0.59 0.11 0 0"
              result="alpha"
            />
            {/* Étape 2 : remplir avec la couleur voulue en utilisant cet alpha */}
            <feFlood floodColor={color} result="flood" />
            <feComposite in="flood" in2="alpha" operator="in" />
          </filter>
          {clipId && (
            <clipPath id={clipId}>
              <rect x={0} y={band.y1} width={vw} height={band.y2 - band.y1} />
            </clipPath>
          )}
        </defs>
        <g
          filter={`url(#${filterId})`}
          clipPath={clipId ? `url(#${clipId})` : undefined}
        >
          <Component />
        </g>
      </svg>
    </div>
  );
}

// ─── Empreinte centrale ───────────────────────────────────────────────────────
function CenterFingerprint({ totalFragments, validatedFrags, allValidated, isPulsing, onBlow, revealProgress, isVisible }) {
  const W = 340, H = 410;
  const baseColor = (allValidated || isPulsing) ? "#34d399" : "#ffffff";
  const { yTop, yBot } = SVG_SOURCES.real;

  const validatedBands = useMemo(() =>
    Object.keys(validatedFrags).map(vk => ({
      band: getBand(Number(vk) - 1, totalFragments, "real"),
      color: isPulsing ? "#34d399" : getColor(Number(vk)),
    })),
    [validatedFrags, totalFragments, isPulsing]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
      <div style={{
        width: W, height: H, borderRadius: 16,
        border: `2px solid ${isPulsing ? "#34d399" : allValidated ? "#34d39966" : "#0a2535"}`,
        background: "rgba(0,4,10,0.97)",
        position: "relative", overflow: "hidden",
        boxShadow: isPulsing
          ? "0 0 80px #34d39960, 0 0 30px #34d39940"
          : allValidated ? "0 0 60px #34d39930"
            : "inset 0 0 40px rgba(0,30,50,0.4)",
        transition: "border-color 0.4s, box-shadow 0.4s",
        animation: isPulsing ? "fp-pulse 0.8s ease-in-out infinite alternate" : "none",
      }}>
        {/* Scanlines */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none", zIndex: 3,
          backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(0,0,0,0.05) 3px,rgba(0,0,0,0.05) 4px)",
        }} />

        {/* Séparateurs de bandes */}
        {totalFragments > 1 && (
          <svg style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 2 }} viewBox={`0 0 ${W} ${H}`} width={W} height={H}>
            {Array.from({ length: totalFragments - 1 }, (_, i) => {
              const y = ((yTop + (i + 1) * (yBot - yTop) / totalFragments) / 800) * H;
              return <line key={i} x1={14} y1={y} x2={W - 14} y2={y} stroke={baseColor} strokeWidth="1" strokeDasharray="6,5" opacity="0.3" />;
            })}
          </svg>
        )}

        {/* Ghost : empreinte entière, opacity liée au souffle ou pleine si pulse */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 0,
          opacity: isPulsing ? 0.9 : isVisible ? revealProgress * 1 : 0,
          transition: isPulsing ? "opacity 0.4s" : isVisible ? "opacity 0.08s" : "opacity 1.5s ease-in",
          pointerEvents: "none",
        }}>
          <BandedSVG srcKey="real" color={baseColor} width={W} height={H} />
        </div>

        {/* Bandes validées — paths dans la couleur du joueur */}
        {validatedBands.map((vb, i) => (
          <div key={i} style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }}>
            <BandedSVG srcKey="real" band={vb.band} color={vb.color} width={W} height={H} />
          </div>
        ))}

        {/* État vide */}
        {!isVisible && !isPulsing && validatedBands.length === 0 && (
          <div style={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 8,
            pointerEvents: "none", zIndex: 0,
          }}>
            <BandedSVG srcKey="real" color="#22d3ee" width={100} height={100} opacity={0.05} />
            <div style={{ fontSize: "9px", color: "#0a2535", letterSpacing: "0.12em" }}>SURFACE VIDE</div>
          </div>
        )}

        {/* Overlay radial vert pulsant */}
        {isPulsing && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 2, pointerEvents: "none",
            background: "radial-gradient(ellipse at center, #34d39922 0%, transparent 70%)",
            animation: "fp-glow 0.8s ease-in-out infinite alternate",
          }} />
        )}

        <div style={{
          position: "absolute", bottom: 7, left: 0, right: 0, textAlign: "center",
          fontSize: "9px", color: (allValidated || isPulsing) ? "#34d399" : "#22d3ee",
          letterSpacing: "0.12em", fontFamily: "monospace", zIndex: 4,
          fontWeight: isPulsing ? 700 : 400,
        }}>
          {(allValidated || isPulsing) ? "✓ EMPREINTE VALIDÉE" : "EMPREINTE CIBLE"}
        </div>
      </div>

      {!allValidated && !isPulsing && (
        <button onClick={onBlow} style={{
          padding: "11px 26px", borderRadius: 8,
          border: "2px solid #22d3ee88",
          background: "rgba(34,211,238,0.12)",
          color: "#22d3ee", fontSize: "11px", fontWeight: 900,
          letterSpacing: "0.15em", cursor: "pointer", fontFamily: "monospace",
          transition: "all 0.2s",
          boxShadow: "0 0 18px rgba(34,211,238,0.2), inset 0 0 12px rgba(34,211,238,0.05)",
          clipPath: "polygon(6px 0%,100% 0%,calc(100% - 6px) 100%,0% 100%)",
          textTransform: "uppercase",
        }}
          onMouseEnter={e => {
            e.currentTarget.style.background = "rgba(34,211,238,0.22)";
            e.currentTarget.style.boxShadow = "0 0 28px rgba(34,211,238,0.4), inset 0 0 16px rgba(34,211,238,0.1)";
            e.currentTarget.style.borderColor = "#22d3ee";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = "rgba(34,211,238,0.12)";
            e.currentTarget.style.boxShadow = "0 0 18px rgba(34,211,238,0.2), inset 0 0 12px rgba(34,211,238,0.05)";
            e.currentTarget.style.borderColor = "#22d3ee88";
          }}
        >
          💨 SOUFFLER SUR L'ÉCRAN
        </button>

      )}
    </div>
  );
}

// ─── Carte fragment ───────────────────────────────────────────────────────────
function FragmentCard({ color, fragIdx, totalFragments, cardIdx, selected, validated, onSelect, disabled, svgKey }) {
  const W = 190, H = 228;
  const band = getBand(fragIdx, totalFragments, svgKey);
  const displayColor = validated ? color : selected ? color : "#ffffff";
  const displayOpacity = validated ? 1 : selected ? 1 : disabled ? 0.2 : 1;

  return (
    <div
      onClick={() => !disabled && !validated && onSelect(cardIdx)}
      style={{
        width: W, height: H, borderRadius: 12,
        cursor: disabled || validated ? "default" : "pointer",
        border: `2px solid ${validated ? color : selected ? color + "99" : "#0a2030"}`,
        background: validated ? `${color}18` : selected ? `${color}10` : "rgba(0,5,12,0.75)",
        boxShadow: validated ? `0 0 28px ${color}44` : selected ? `0 0 16px ${color}30` : "none",
        transition: "all 0.2s", position: "relative", overflow: "hidden",
      }}
    >
      <BandedSVG srcKey={svgKey} band={band} color={displayColor} width={W} height={H} opacity={displayOpacity} />
      {selected && !validated && (
        <div style={{
          position: "absolute", top: 7, right: 7,
          width: 10, height: 10, borderRadius: "50%",
          background: color, boxShadow: `0 0 8px ${color}`,
          pointerEvents: "none",
        }} />
      )}
      {validated && (
        <div style={{
          position: "absolute", top: 6, left: 0, right: 0,
          textAlign: "center", fontSize: "14px", color, fontFamily: "monospace", fontWeight: 700,
          pointerEvents: "none",
        }}>✓</div>
      )}
    </div>
  );
}

// ─── DoorPad principal ────────────────────────────────────────────────────────
export default function DoorPad({ myVariant, playerCount, doorState, onSelectFragment, onStart, serverComplete, onValidationComplete }) {
  const [revealProgress, setRevealProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [localSel, setLocalSel] = useState(null);
  const [isPulsing, setIsPulsing] = useState(false);
  const revealRef = useRef(null);
  const hideRef = useRef(null);
  const pulseRef = useRef(null);

  const unlocked = doorState?.unlocked === true;
  const started = doorState?.started === true;
  const validated = doorState?.validated || {};
  const failed = doorState?.failed === true;
  const myColor = getColor(myVariant);
  const isMyDone = validated[String(myVariant)] === true;
  const allValidated = Object.keys(validated).length === playerCount && playerCount > 0;

  const myCards = useMemo(() => {
    const myFragIdx = Number(myVariant) - 1;
    let s = ((Number(myVariant) * 137 + playerCount * 31) | 0) >>> 0;
    const rand = () => { s = (Math.imul(1664525, s) + 1013904223) >>> 0; return s / 0x100000000; };

    const pool = Array.from({ length: Math.max(playerCount, 4) }, (_, i) => i);
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    const distractors = pool.filter(i => i !== myFragIdx).slice(0, 3);
    while (distractors.length < 3) distractors.push(distractors.length); // fallback

    const fakeKeys = ["fake3", "fake3", "fake3"];

    const cards = [
      { fragIdx: myFragIdx, isCorrect: true, svgKey: "real" },
      { fragIdx: distractors[0], isCorrect: false, svgKey: fakeKeys[0] },
      { fragIdx: distractors[1], isCorrect: false, svgKey: fakeKeys[1] },
      { fragIdx: distractors[2], isCorrect: false, svgKey: fakeKeys[2] },
    ];

    // Mélanger
    for (let i = cards.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }
    return cards;
  }, [myVariant, playerCount]);

  useEffect(() => { if (failed) setLocalSel(null); }, [failed]);
  useEffect(() => {
    if (allValidated && !isPulsing) {
      clearTimeout(hideRef.current);
      setIsVisible(true);
      setRevealProgress(1);
      setIsPulsing(true);
      // Après 3.5s d'animation, appeler le callback de fin
      pulseRef.current = setTimeout(() => {
        setIsPulsing(false);
        onValidationComplete?.();
      }, 3500);
    }
    return () => clearTimeout(pulseRef.current);
  }, [allValidated]);

  const handleBlow = useCallback(() => {
    if (unlocked || !started) return;
    clearInterval(revealRef.current); clearTimeout(hideRef.current);
    setIsVisible(true); setRevealProgress(0);
    const t0 = Date.now();
    revealRef.current = setInterval(() => {
      const t = Math.min(1, (Date.now() - t0) / 1400);
      setRevealProgress(t);
      if (t >= 1) clearInterval(revealRef.current);
    }, 24);
    hideRef.current = setTimeout(() => { setIsVisible(false); setRevealProgress(0); }, 11400);
  }, [unlocked, started]);

  useEffect(() => () => { clearInterval(revealRef.current); clearTimeout(hideRef.current); clearTimeout(pulseRef.current); }, []);

  const handleConfirm = useCallback(() => {
    if (localSel === null || isMyDone) return;
    const card = myCards[localSel];
    onSelectFragment?.({ cardPos: localSel, isCorrect: card.isCorrect, confirm: true });
  }, [localSel, isMyDone, myCards, onSelectFragment]);

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "100%", width: "100%",
      fontFamily: "'Courier New', monospace",
      background: "#020810", color: "#e2e8f0", overflow: "hidden",
    }}>
      <style>{`
        @keyframes fadein { from { opacity:0; transform:scale(.93) } to { opacity:1; transform:scale(1) } }
        @keyframes shake  { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 60%{transform:translateX(6px)} }
        @keyframes fp-pulse {
          from { box-shadow: 0 0 40px #34d39940, 0 0 15px #34d39930; border-color: #34d39988; }
          to   { box-shadow: 0 0 100px #34d39980, 0 0 50px #34d39960; border-color: #34d399ff; }
        }
        @keyframes fp-glow {
          from { opacity: 0.4; }
          to   { opacity: 1; }
        }
        .doorpad-scroll { scrollbar-width: thin; scrollbar-color: #0d4060 #020810; }
        .doorpad-scroll::-webkit-scrollbar { width: 6px; }
        .doorpad-scroll::-webkit-scrollbar-track { background: #020810; }
        .doorpad-scroll::-webkit-scrollbar-thumb { background: #0d4060; border-radius: 4px; }
        .doorpad-scroll::-webkit-scrollbar-thumb:hover { background: #1a6a8a; }
      `}</style>

      {/* Header */}
      <div style={{
        flexShrink: 0, padding: "10px 18px",
        borderBottom: "1px solid #0a1628", background: "#030c1a",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: unlocked ? "#34d399" : "#f97316", boxShadow: `0 0 8px ${unlocked ? "#34d399" : "#f97316"}` }} />
          <span style={{ fontSize: "11px", letterSpacing: "0.16em", fontWeight: 700, color: unlocked ? "#34d399" : "#f97316" }}>
            {unlocked ? "PORTE DÉVERROUILLÉE" : "PAD BIOMÉTRIQUE — IDENTIFICATION REQUISE"}
          </span>
        </div>
        <span style={{ fontSize: "10px", color: "#1a4a60", letterSpacing: "0.1em" }}>
          {Object.keys(validated).length}/{playerCount} VALIDÉS
        </span>
      </div>

      {!serverComplete ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: "9px", color: "#1a3a4a", letterSpacing: "0.2em", textAlign: "center" }}>
            ACCÈS REFUSÉ<br /><span style={{ opacity: 0.6 }}>Le pad doit être reconfiguré depuis le serveur</span>
          </div>
        </div>

      ) : unlocked ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
          <CenterFingerprint totalFragments={playerCount} validatedFrags={validated} allValidated onBlow={() => { }} revealProgress={1} isVisible />
          <div style={{ fontSize: "18px", fontWeight: 900, letterSpacing: "0.25em", color: "#34d399" }}>ACCÈS ACCORDÉ</div>
        </div>

      ) : !started ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
          <div style={{ fontSize: "12px", color: "#1a4a60", letterSpacing: "0.15em", textAlign: "center", lineHeight: 2 }}>
            SYSTÈME D'IDENTIFICATION BIOMÉTRIQUE<br />
            <span style={{ fontSize: "10px", opacity: 0.6 }}>Chaque joueur identifie son fragment dans l'empreinte cible</span>
          </div>
          <button onClick={() => onStart?.()} style={{
            padding: "14px 32px", fontSize: "13px", fontWeight: 700, letterSpacing: "0.14em",
            borderRadius: 10, cursor: "pointer", background: "rgba(34,211,238,0.1)",
            border: "1px solid #22d3ee44", color: "#22d3ee", fontFamily: "monospace", transition: "all 0.2s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(34,211,238,0.2)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(34,211,238,0.1)"}
          >▶ LANCER L'IDENTIFICATION</button>
        </div>

      ) : (
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Gauche : empreinte cible */}
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            padding: "20px 24px", borderRight: "1px solid #0a1628", flexShrink: 0, gap: 12,
          }}>
            <div style={{ fontSize: "12px", color: "#22d3ee", letterSpacing: "0.16em", fontWeight: 700 }}>EMPREINTE CIBLE</div>
            <CenterFingerprint
              totalFragments={playerCount} validatedFrags={validated} allValidated={allValidated}
              isPulsing={isPulsing}
              onBlow={handleBlow} revealProgress={revealProgress} isVisible={isVisible}
            />
            {failed && (
              <div style={{
                padding: "7px 14px", borderRadius: 6,
                background: "rgba(239,68,68,0.12)", border: "1px solid #ef444444",
                color: "#ef4444", fontSize: "10px", letterSpacing: "0.1em",
                textAlign: "center", animation: "shake 0.4s ease",
              }}>⚠ FRAGMENT INCORRECT</div>
            )}
          </div>

          {/* Droite : mes 4 cartes */}
          <div className="doorpad-scroll" style={{
            flex: 1, display: "flex", flexDirection: "column",
            padding: "20px 22px", gap: 16, overflowY: "auto",
          }}>
            <div>
              <div style={{ fontSize: "13px", color: myColor, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 4 }}>
                MON FRAGMENT — J{myVariant}
              </div>
              <div style={{ fontSize: "12px", color: "#22d3ee", letterSpacing: "0.08em" }}>
                {isMyDone ? "✓ VALIDÉ" : "Identifiez votre portion dans l'empreinte cible"}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {myCards.map((card, ci) => (
                <div key={ci} style={{ animation: `fadein 0.25s ease ${ci * 0.07}s both`, display: "flex", justifyContent: "center" }}>
                  <FragmentCard
                    color={myColor} fragIdx={card.fragIdx} totalFragments={playerCount}
                    cardIdx={ci} selected={localSel === ci}
                    validated={isMyDone && card.isCorrect}
                    onSelect={idx => !isMyDone && setLocalSel(idx)}
                    disabled={isMyDone} svgKey={card.svgKey}
                  />
                </div>
              ))}
            </div>

            {!isMyDone && (
              <button onClick={handleConfirm} disabled={localSel === null} style={{
                width: "100%", height: 52, borderRadius: 10,
                cursor: localSel !== null ? "pointer" : "not-allowed",
                background: localSel !== null ? `linear-gradient(135deg,${myColor}18,${myColor}30)` : "#001122",
                border: `2px solid ${localSel !== null ? myColor + "55" : "#001a2a"}`,
                color: localSel !== null ? myColor : "#002233",
                fontSize: "13px", fontWeight: 900, letterSpacing: "0.14em",
                fontFamily: "monospace", transition: "all 0.2s",
              }}>
                {localSel !== null ? "⬡ VALIDER CE FRAGMENT" : "— SÉLECTIONNER —"}
              </button>
            )}

            <div>
              <div style={{ fontSize: "9px", color: "#22d3ee", letterSpacing: "0.1em", marginBottom: 8, fontWeight: 700 }}>STATUT ÉQUIPE</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {Array.from({ length: playerCount }, (_, i) => {
                  const v = i + 1; const c = getColor(v); const done = validated[String(v)] === true;
                  return (
                    <div key={v} style={{
                      padding: "5px 12px", borderRadius: 5,
                      border: `1px solid ${done ? c + "70" : "#0a1e2a"}`,
                      background: done ? `${c}12` : "rgba(0,0,0,0.2)",
                      fontSize: "11px", fontWeight: 700, color: done ? c : "#22d3ee",
                      display: "flex", alignItems: "center", gap: 6,
                    }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: done ? c : "#22d3ee" }} />
                      J{v}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}